import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ALWAYS_ALLOWED = new Set([
  'integrations', 'settings', 'workspace', 'admin/users', 'admin/orgs', 'sites',
]);

interface ViewingOrg {
  org_id: number;
  org_name: string;
  licensed_modules: string[];
  org_flags?: Record<string, boolean>;
}

interface LicenseContextValue {
  isSuperAdmin: boolean;
  viewingOrg: ViewingOrg | null;
  licensedModules: string[];
  hasModule: (slug: string) => boolean;
  switchOrg: (orgId: number) => Promise<void>;
  resetOrg: () => void;
  allOrgs: { id: number; name: string }[];
  loadOrgs: () => Promise<void>;
  isDemoOrg: boolean;
  isProtegeOrg: boolean;
  currentOrgName: string;
}

const LicenseContext = createContext<LicenseContextValue | null>(null);

export function useLicense() {
  const ctx = useContext(LicenseContext);
  if (!ctx) throw new Error('useLicense must be used within LicenseProvider');
  return ctx;
}

const getToken = () => localStorage.getItem('lemai_token') || '';

export function LicenseProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [viewingOrg, setViewingOrg] = useState<ViewingOrg | null>(null);
  const [allOrgs, setAllOrgs] = useState<{ id: number; name: string }[]>([]);

  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const licensedModules: string[] = (user as any)?.licensed_modules || [];

  // Clear viewingOrg when user changes
  useEffect(() => {
    setViewingOrg(null);
  }, [(user as any)?.id]);

  // Load orgs list for super admin
  async function loadOrgs() {
    if (!isSuperAdmin) return;
    try {
      const r = await fetch('/api/admin/orgs', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (r.ok) {
        const d = await r.json();
        setAllOrgs((d.orgs || []).map((o: any) => ({ id: o.id, name: o.name })));
      }
    } catch {}
  }

  useEffect(() => {
    if (isSuperAdmin) loadOrgs();
  }, [isSuperAdmin]);

  async function switchOrg(orgId: number) {
    try {
      const r = await fetch('/api/admin/switch-org', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ org_id: orgId }),
      });
      if (r.ok) {
        const d = await r.json();
        setViewingOrg({
          org_id: d.org_id,
          org_name: d.org_name,
          licensed_modules: d.licensed_modules || [],
        });
      }
    } catch {}
  }

  function resetOrg() {
    setViewingOrg(null);
  }

  function hasModule(slug: string): boolean {
    if (ALWAYS_ALLOWED.has(slug)) return true;
    if (isSuperAdmin && !viewingOrg) return true; // super admin sees all
    const modules = viewingOrg ? viewingOrg.licensed_modules : licensedModules;
    return modules.includes(slug);
  }

  // Resolve current org name (lower-case) from viewing override or logged-in user
  const currentOrgName: string = (
    viewingOrg ? viewingOrg.org_name : (user as any)?.org_name || ''
  ).toLowerCase().trim();

  // Resolve org flags — prefer explicit DB flags, fall back to name-matching for legacy orgs
  const orgFlags: Record<string, boolean> = (
    viewingOrg ? viewingOrg.org_flags : (user as any)?.org_flags
  ) || {};

  // isDemoOrg — flag takes precedence; name 'demo' retained as legacy fallback
  const isDemoOrg: boolean = orgFlags.is_demo === true
    || currentOrgName === 'demo'
    || currentOrgName.startsWith('demo ');

  // isProtegeOrg — flag takes precedence; name-matching removed
  const isProtegeOrg: boolean = orgFlags.protege_enabled === true;

  return (
    <LicenseContext.Provider value={{
      isSuperAdmin,
      viewingOrg,
      licensedModules,
      hasModule,
      switchOrg,
      resetOrg,
      allOrgs,
      loadOrgs,
      isDemoOrg,
      isProtegeOrg,
      currentOrgName,
    }}>
      {children}
    </LicenseContext.Provider>
  );
}
